import { formatOrderDateTime } from "../../utils/orderFinalUpdate";
import {
  getPaymentSubStatusHeadline,
  normalizePaymentStatus,
  resolvePaymentBadgeStatus,
} from "../../utils/orderDisplayStatus";

const HEADLINE_DATETIME_OPTIONS = {
  weekday: "long",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

export const STEP_DATETIME_OPTIONS = {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

/** Order lifecycle: Created → Approved → Bill Generated → Paid → Packed → Shipped → Delivered */
export const TRACKING_STEPS = [
  { key: "ordered", label: "Created" },
  { key: "approved", label: "Shipment Approved" },
  { key: "bill_generated", label: "Bill Generated" },
  { key: "paid", label: "Paid" },
  { key: "packing", label: "Packed" },
  { key: "shipment", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

const LAST_STEP_INDEX = TRACKING_STEPS.length - 1;

/**
 * Index of the last completed step (0–5). -1 = cancelled / rejected.
 */
export function getLastCompletedTrackingIndex(orderStatus) {
  const s = String(orderStatus || "").toLowerCase();
  if (s === "cancelled" || s === "rejected") return -1;
  if (s === "delivered") return 6;
  if (s === "shipment" || s === "wait_to_deliver" || s === "un_delivered") return 5;
  if (s === "packing" || s === "recieved") return 4;
  if (s === "paid") return 3;
  if (s === "bill_generated") return 2;
  if (s === "approved") return 1;
  if (s === "pending" || s === "request_info" || s === "created") return 0;
  return 0;
}

/** Map API `tracking_stage_timestamps` to each step key (with client fallbacks). */
export function getTrackingStepTimestamps(order) {
  const fromApi = order?.tracking_stage_timestamps;
  const map = fromApi && typeof fromApi === "object" ? { ...fromApi } : {};
  const lastIdx = getLastCompletedTrackingIndex(order?.order_status);
  const createdAt = order?.createdAt ?? null;
  const updatedAt = order?.updatedAt ?? null;

  if (!map.ordered && createdAt) {
    map.ordered = createdAt;
  }

  // Use final_update_sent_at as the exact timestamp for bill_generated step
  if (!map.bill_generated && order?.final_update_sent_at) {
    map.bill_generated = order.final_update_sent_at;
  }

  if (lastIdx >= 0) {
    const currentKey = TRACKING_STEPS[lastIdx]?.key;
    if (currentKey && !map[currentKey] && updatedAt) {
      map[currentKey] = updatedAt;
    }

    for (let i = 0; i <= lastIdx; i += 1) {
      const key = TRACKING_STEPS[i]?.key;
      if (!key || map[key]) continue;
      if (i === 0 && createdAt) {
        map[key] = createdAt;
        continue;
      }
      if (i === lastIdx && updatedAt) {
        map[key] = updatedAt;
        continue;
      }
      for (let j = i + 1; j <= lastIdx; j += 1) {
        const laterKey = TRACKING_STEPS[j]?.key;
        if (laterKey && map[laterKey]) {
          map[key] = map[laterKey];
          break;
        }
      }
      if (!map[key] && createdAt) {
        map[key] = createdAt;
      }
    }
  }

  return TRACKING_STEPS.reduce((acc, step) => {
    acc[step.key] = map[step.key] ?? null;
    return acc;
  }, {});
}

export function formatStepDateTime(iso) {
  if (!iso) return null;
  return formatOrderDateTime(iso, STEP_DATETIME_OPTIONS);
}

export function getStatusHeadline(order) {
  if (!order) return { title: "", sub: "" };
  const s = String(order.order_status || "").toLowerCase();
  const orderedAt =
    formatOrderDateTime(order.createdAt, HEADLINE_DATETIME_OPTIONS) || "";

  if (s === "delivered") {
    const at =
      formatOrderDateTime(order.updatedAt, HEADLINE_DATETIME_OPTIONS) || "";
    return {
      title: at ? `Delivered ${at}` : "Delivered",
      sub: "Your order has been completed.",
    };
  }
  if (s === "shipment" || s === "wait_to_deliver") {
    return {
      title: "Shipped",
      sub: "Your package is on the way.",
    };
  }
  if (s === "un_delivered") {
    return {
      title: "Delivery attempted",
      sub: "We could not complete delivery. Our team will follow up.",
    };
  }
  if (s === "packing" || s === "recieved") {
    if (normalizePaymentStatus(order.payment_status) === "reject_shipment") {
      return (
        getPaymentSubStatusHeadline(order) ?? {
          title: "Packed",
          sub: "Shipment rejected.",
        }
      );
    }
    return {
      title: "Packed",
      sub: "We're preparing your items for shipment.",
    };
  }
  if (s === "paid") {
    if (normalizePaymentStatus(order.payment_status) === "unpacked") {
      return (
        getPaymentSubStatusHeadline(order) ?? {
          title: "Unpacked",
          sub: "Items unpacked. Ready to pack again when needed.",
        }
      );
    }
    if (normalizePaymentStatus(order.payment_status) === "reject_shipment") {
      return (
        getPaymentSubStatusHeadline(order) ?? {
          title: "Paid",
          sub: "Shipment rejected.",
        }
      );
    }
    return {
      title: "Paid",
      sub: "Payment received. Your order will move to packing.",
    };
  }
  if (s === "bill_generated") {
    return {
      title: "Bill Generated",
      sub: "Your final bill has been sent. Please complete the payment.",
    };
  }
  if (s === "approved") {
    const subStatusHeadline = getPaymentSubStatusHeadline(order);
    if (subStatusHeadline) {
      return subStatusHeadline;
    }
    return {
      title: "Approved",
      sub: "Your order is approved and awaiting payment.",
    };
  }
  if (s === "cancelled") {
    return { title: "Order cancelled", sub: "This order is no longer active." };
  }
  if (s === "rejected") {
    return { title: "Order rejected", sub: "This order could not be fulfilled." };
  }
  if (s === "pending" || s === "created" || s === "request_info") {
    return {
      title: orderedAt ? `Created ${orderedAt}` : "Created",
      sub: "We're processing your order.",
    };
  }
  return {
    title: orderedAt ? `Created ${orderedAt}` : "Created",
    sub: "We're processing your order.",
  };
}

export function resolveOrderPaymentStatus(order) {
  return resolvePaymentBadgeStatus(order);
}

export { LAST_STEP_INDEX };
